import React, { useEffect, useMemo, useState } from 'react'
import { DatePicker, Form, Input, Select } from 'antd'
import moment from 'moment'
import { Modal } from '@/components/dialog/modal'
import $msg from '@/utils/message'
import { IMember, IMemberGroupListItem } from '@/model/types/member'
import { editMember } from '../api'

interface EditMemberForm {
    name?: string
    phone: string
    birthday?: moment.Moment
    group_id?: number
    card_code?: string
}

interface Props {
    visible: boolean
    member: IMember
    groups: IMemberGroupListItem[]
    onCancel: () => void
    onSuccess: () => void
}

const EditMemberModal: React.FC<Props> = props => {
    const [form] = Form.useForm<EditMemberForm>()
    const [loading, setLoading] = useState(false)
    const phoneEditable = !props.member.user

    const birthday = useMemo(() => {
        if (!props.member.birthday) return undefined

        return moment(props.member.birthday)
    }, [props.member.birthday])

    useEffect(() => {
        if (!props.visible) return

        form.setFieldsValue({
            name: props.member.name,
            phone: props.member.phone,
            birthday,
            group_id: props.member.group?.id,
            card_code: props.member.card_code,
        })
    }, [birthday, form, props.member, props.visible])

    const onSubmit = async () => {
        try {
            const values = await form.validateFields()
            setLoading(true)
            await editMember({
                member_id: props.member.id,
                name: values.name,
                phone: phoneEditable ? values.phone : props.member.phone,
                birthday: values.birthday ? values.birthday.format('YYYY-MM-DD') : undefined,
                group_id: values.group_id,
                card_code: values.card_code || '',
            })
            $msg('success', '编辑会员成功')
            props.onSuccess()
        } catch (e) {
            if (e && e.errorFields) return
            $msg('error', e.message || '编辑会员失败')
        } finally {
            setLoading(false)
        }
    }

    return props.visible ? (
        <Modal
            title="编辑会员"
            isShow
            type="success"
            loading={loading}
            onConfirm={onSubmit}
            onClose={props.onCancel}
            onCancel={props.onCancel}
            okText="确认"
            cancelText="取消"
        >
            <Form form={form} layout="vertical">
                <Form.Item name="name" label="会员姓名">
                    <Input maxLength={20} placeholder="请输入会员姓名" />
                </Form.Item>
                <Form.Item
                    name="phone"
                    label="手机号"
                    rules={[
                        { required: true, message: '请输入手机号' },
                        { pattern: /^1\d{10}$/, message: '请输入正确的手机号' },
                    ]}
                >
                    <Input placeholder="请输入手机号" maxLength={11} disabled={!phoneEditable} />
                </Form.Item>
                <Form.Item name="birthday" label="生日">
                    <DatePicker allowClear={false} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item
                    name="card_code"
                    label="会员卡号"
                    rules={[{ pattern: /^\d{4,12}$/, message: '会员卡号需为4-12位数字' }]}
                >
                    <Input maxLength={12} placeholder="请输入会员卡号" />
                </Form.Item>
                <Form.Item name="group_id" label="会员分组">
                    <Select placeholder="请选择会员分组">
                        {props.groups.map(group => (
                            <Select.Option key={group.id} value={group.id}>
                                {group.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    ) : null
}

export default EditMemberModal
